import { useCallback, useState } from 'react';
import styled from 'styled-components';

import { useQueryClient } from '@tanstack/react-query';

import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDate } from 'lib/date';
import { LocationEpisodeLink } from 'models/flexible/LocationEpisodeLink';
import { LocationEpisodeLinkSet } from 'models/flexible/LocationEpisodeLinkSet';
import { adminIndexClients } from 'services/api/admin/clients';
import {
  adminIndexLinkedRecords,
  adminLinkedRecordsQueryKeys,
  updateLinkedRecord,
  updateLocationEpisodeLink,
} from 'services/api/admin/linkedRecords';
import type { AdminLinkSetIndexFields } from 'services/api/fields';
import { colors } from 'styles/theme/colors';
import { BodySmall, BodySmallBold } from 'styles/typography';
import PencilIcon from 'svg/PencilIcon';
import UnlinkIcon from 'svg/UnlinkIcon';

import LinkDetailsSlideout from './LinkDetailsSlideout';
import StatusBadge from './StatusBadge';
import UnlinkConfirmationModal from './UnlinkConfirmationModal';

const INCLUDES = [] as const;

const config: AdminListPageConfig<LocationEpisodeLinkSet<AdminLinkSetIndexFields<(typeof INCLUDES)[number]>>> = {
  title: 'Shared Records',
  queryKey: adminLinkedRecordsQueryKeys.index,
  fetchFn: (params, signal) => adminIndexLinkedRecords(params, signal, [...INCLUDES]),
  includes: INCLUDES,
  fetchParams: {
    defaultSort: 'patient_name asc',
    pageSize: 10,
  },
  searchPlaceholder: 'Search Patients',
  showResultCount: true,

  filters: [
    {
      key: 'clientId',
      label: 'Client',
      type: 'dropdown',
      fetchConfig: {
        queryKey: ['adminClients', 'linkedRecords', 'filter'],
        fetchFn: adminIndexClients,
        params: { sortBy: 'name asc' },
      },
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
      toRequest: (value) => value?.id || undefined,
    },
    {
      key: 'archived',
      label: 'Include Unlinked Records',
      type: 'checkbox',
      toRequest: (value) => (value ? undefined : 'false'),
    },
    {
      key: 'isPendingReview',
      label: 'Show Only Pending Review',
      type: 'checkbox',
      toRequest: (value) => (value ? 'true' : undefined),
    },
  ],

  columns: [
    {
      id: 'patient_name',
      header: 'Patient',
      render: (row) => {
        const textColor = row.isUnlinked ? colors.black50 : undefined;
        return (
          <PatientCell>
            <BodySmallBold style={{ color: textColor }}>{row.patientName}</BodySmallBold>
            {row.groupName && <BodySmall style={{ color: colors.black50 }}>{row.groupName}</BodySmall>}
          </PatientCell>
        );
      },
    },
    {
      id: 'link_count',
      header: 'Link Set Count',
      sortable: false,
      render: (row, ctx) => {
        if (row.isUnlinked) {
          return <span style={{ color: colors.black50 }}>{row.linkCount}</span>;
        }
        return (
          <PatientCountLink onClick={() => ctx.openSlideout('linkDetails', row)}>{row.linkCount}</PatientCountLink>
        );
      },
    },
    {
      id: 'status',
      header: 'Status',
      sortable: false,
      render: (row) => <StatusBadge status={row.status} />,
    },
    {
      id: 'created_at',
      header: 'Linked On',
      render: (row) => {
        const text = row.status === 'linked' ? simpleDate(row.createdAt) : '\u2014';
        return <span style={row.isUnlinked ? { color: colors.black50 } : undefined}>{text}</span>;
      },
    },
  ],

  rowActions: [
    {
      label: 'View Link Details',
      icon: <PencilIcon width={18} height={18} />,
      permission: 'adminLinkedRecordView',
      onClick: (row, ctx) => ctx.openSlideout('linkDetails', row),
    },
    {
      label: 'Unlink Records',
      icon: <UnlinkIcon size={18} />,
      permission: 'adminLinkedRecordEdit',
      visible: (row) => !row.isUnlinked,
      onClick: (row, ctx) => ctx.openModal('unlink', row),
    },
    {
      label: 'Relink Records',
      icon: <UnlinkIcon size={18} />,
      permission: 'adminLinkedRecordEdit',
      visible: (row) => row.isUnlinked,
      onClick: async (row, ctx) => {
        await updateLinkedRecord(row.id, { archived: false });
        ctx.refetch();
      },
    },
  ],

  modals: {
    unlink: {
      actionText: 'Unlink',
      objType: 'records',
      onConfirm: async (row) => {
        await updateLinkedRecord(row.id, { archived: true });
      },
    },
  },

  slideouts: {
    linkDetails: {
      render: (_row, _onClose) => null, // Handled separately below
    },
  },
};

export default function LinkedRecords() {
  const state = useAdminListPage(config);
  const { refetch } = state;
  const queryClient = useQueryClient();

  const [slideoutRecord, setSlideoutRecord] = useState<LocationEpisodeLinkSet<AdminLinkSetIndexFields> | null>(null);
  const [unlinkRecord, setUnlinkRecord] = useState<LocationEpisodeLinkSet<AdminLinkSetIndexFields> | null>(null);
  const [removeLink, setRemoveLink] = useState<LocationEpisodeLink | null>(null);

  // Override slideout behavior to use our custom slideout
  const originalOpenSlideout = state.openSlideout;
  const handleOpenSlideout = useCallback(
    (name: string, row: any) => {
      if (name === 'linkDetails') {
        setSlideoutRecord(row);
      } else {
        originalOpenSlideout(name, row);
      }
    },
    [originalOpenSlideout]
  );

  // Override modal behavior for unlink
  const originalOpenModal = state.openModal;
  const handleOpenModal = useCallback(
    (name: string, row: any) => {
      if (name === 'unlink') {
        setUnlinkRecord(row);
      } else {
        originalOpenModal(name, row);
      }
    },
    [originalOpenModal]
  );

  const handleCloseSlideout = useCallback(() => {
    setSlideoutRecord(null);
  }, []);

  const handleUnlink = useCallback((record: LocationEpisodeLinkSet<AdminLinkSetIndexFields>) => {
    setUnlinkRecord(record);
  }, []);

  const handleRelink = useCallback(
    async (record: LocationEpisodeLinkSet<AdminLinkSetIndexFields>) => {
      await updateLinkedRecord(record.id, { archived: false });
      refetch();
    },
    [refetch]
  );

  const handleConfirmUnlink = useCallback(async () => {
    if (unlinkRecord) {
      await updateLinkedRecord(unlinkRecord.id, { archived: true });
      setUnlinkRecord(null);
      setSlideoutRecord(null);
      refetch();
    }
  }, [unlinkRecord, refetch]);

  const handleCancelUnlink = useCallback(() => {
    setUnlinkRecord(null);
  }, []);

  const handleRemoveLink = useCallback((link: LocationEpisodeLink) => {
    setRemoveLink(link);
  }, []);

  const invalidateDetail = useCallback(() => {
    if (slideoutRecord) {
      queryClient.invalidateQueries({ queryKey: adminLinkedRecordsQueryKeys.show(slideoutRecord.id) });
    }
  }, [slideoutRecord, queryClient]);

  const handleConfirmRemove = useCallback(async () => {
    if (removeLink) {
      await updateLocationEpisodeLink(removeLink.id, { archived: true });
      setRemoveLink(null);
      refetch();
      invalidateDetail();
    }
  }, [removeLink, refetch, invalidateDetail]);

  const handleCancelRemove = useCallback(() => {
    setRemoveLink(null);
  }, []);

  const handleApproveLink = useCallback(
    async (linkId: string) => {
      await updateLocationEpisodeLink(linkId, { status: 'active' });
      refetch();
      invalidateDetail();
    },
    [refetch, invalidateDetail]
  );

  const handleRejectLink = useCallback(
    async (linkId: string) => {
      await updateLocationEpisodeLink(linkId, { archived: true });
      refetch();
      invalidateDetail();
    },
    [refetch, invalidateDetail]
  );

  const handleApplyChanges = useCallback(
    async (approveIds: string[], removeIds: string[]) => {
      await Promise.all(approveIds.map((id) => updateLocationEpisodeLink(id, { status: 'active' })));
      await Promise.all(removeIds.map((id) => updateLocationEpisodeLink(id, { archived: true })));
      refetch();
      invalidateDetail();
    },
    [refetch, invalidateDetail]
  );

  // Patch state with our custom handlers
  const patchedState = {
    ...state,
    openSlideout: handleOpenSlideout,
    openModal: handleOpenModal,
  };

  return (
    <>
      <AdminListPageRenderer config={config} state={patchedState} />

      <LinkDetailsSlideout
        record={slideoutRecord}
        onClose={handleCloseSlideout}
        onUnlink={handleUnlink}
        onRelink={handleRelink}
        onApproveLink={handleApproveLink}
        onRejectLink={handleRejectLink}
        onRemoveLink={handleRemoveLink}
        onApplyChanges={handleApplyChanges}
      />

      <UnlinkConfirmationModal record={unlinkRecord} onCancel={handleCancelUnlink} onConfirm={handleConfirmUnlink} />

      <UnlinkConfirmationModal
        variant='remove'
        record={removeLink}
        onCancel={handleCancelRemove}
        onConfirm={handleConfirmRemove}
      />
    </>
  );
}

const PatientCell = styled.div`
  display: flex;
  flex-direction: column;
`;

const PatientCountLink = styled.button`
  background: none;
  border: none;
  padding: 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.primaryBlue};
  cursor: pointer;
  text-decoration: underline;

  &:hover {
    opacity: 0.75;
  }
`;
